import axios from 'axios';

/**
 * La única puerta del portal contra el backend.
 *
 * Todo lo que pide este frontend cuelga de /api/portal. No es una costumbre: es la frontera de
 * seguridad. Al rol CLIENTE el backend le niega cualquier otra ruta (SecurityConfig), así que
 * si algún día alguien escribe aquí una llamada a /api/tramites, va a fallar con 403 en vez de
 * traer datos de más. Es lo que se quiere: que el error salte, no que se filtre.
 */
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8080',
});

/** Dónde se guarda la sesión. Nombre propio para no pisar la del sistema interno si alguien
 *  abriera los dos en el mismo navegador con el mismo dominio. */
const LLAVE_TOKEN = 'portal-wf-token';
const LLAVE_SESION = 'portal-wf-sesion';

export const guardarSesion = (datos) => {
  localStorage.setItem(LLAVE_TOKEN, datos.token);
  localStorage.setItem(LLAVE_SESION, JSON.stringify({
    correo: datos.correo,
    nombre: datos.nombre,
    empresa: datos.empresa,
    debeCambiarPassword: datos.debeCambiarPassword,
  }));
};

export const leerSesion = () => {
  try {
    const crudo = localStorage.getItem(LLAVE_SESION);
    return crudo ? JSON.parse(crudo) : null;
  } catch {
    return null;
  }
};

export const hayToken = () => !!localStorage.getItem(LLAVE_TOKEN);

export const cerrarSesion = () => {
  localStorage.removeItem(LLAVE_TOKEN);
  localStorage.removeItem(LLAVE_SESION);
};

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(LLAVE_TOKEN);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * Si el backend dice que la sesión ya no vale, se cierra y se vuelve al login.
 *
 * Pasa de verdad y no es un caso raro: la sesión se invalida sola al cambiar la contraseña, al
 * pedir una nueva por correo, o si en WF desactivan la cuenta. Sin esto el cliente se queda
 * viendo una pantalla que ya no carga nada y sin entender por qué.
 */
api.interceptors.response.use(
  (respuesta) => respuesta,
  (error) => {
    if (error?.response?.status === 401 && !window.location.pathname.startsWith('/ingresar')) {
      cerrarSesion();
      window.location.href = '/ingresar';
    }
    return Promise.reject(error);
  }
);

/** Saca el mensaje que mandó el backend; si no vino ninguno, uno de repuesto. */
export const mensajeDeError = (error, porDefecto) =>
  error?.response?.data?.message || porDefecto;

// ── Entrar y salir ─────────────────────────────────────────────────────────

export const entrar = (correo, password) =>
  api.post('/api/portal/auth/login', { correo, password });

export const cambiarPassword = (nuevaPassword) =>
  api.put('/api/portal/auth/cambiar-password', { nuevaPassword });

export const pedirPasswordNueva = (correo) =>
  api.post('/api/portal/auth/recuperar', { correo });

// ── Los trámites ───────────────────────────────────────────────────────────

/**
 * Mis tramites. Todo lo que se manda son FILTROS sobre lo mio.
 *
 * Los que van vacios se mandan como `undefined` para que axios ni los incluya en la direccion:
 * un `desde=` vacio llegaria al backend como cadena vacia y reventaria al convertirlo a fecha.
 */
export const misTramites = ({ buscar, desde, hasta, terminado, pagina, porPagina }) =>
  api.get('/api/portal/tramites', {
    params: {
      buscar: buscar || undefined,
      desde: desde || undefined,
      hasta: hasta || undefined,
      terminado: terminado === null || terminado === undefined ? undefined : terminado,
      pagina,
      porPagina,
    },
  });

/** Cuantos tramites tengo y cuantos terminaron. No le afectan los filtros. */
export const resumenDeTramites = () => api.get('/api/portal/resumen');

export const documentosDe = (tramiteId) =>
  api.get(`/api/portal/tramites/${tramiteId}/documentos`);

export const facturasDe = (tramiteId) =>
  api.get(`/api/portal/tramites/${tramiteId}/facturas`);

/** El detalle de un previo del cliente: lo del operario, lo del transportista y las fotos. */
export const previoDe = (previoId) => api.get(`/api/portal/previos/${previoId}`);

// ── Avisos en el dispositivo ───────────────────────────────────────────────

export const configuracionDeAvisos = () =>
  api.get('/api/portal/notificaciones/configuracion');

export const guardarSuscripcion = (suscripcion) =>
  api.post('/api/portal/notificaciones/suscripciones', suscripcion);

export const desactivarSuscripcion = (endpoint) =>
  api.post('/api/portal/notificaciones/suscripciones/desactivar', { endpoint });

/**
 * Abre un archivo en una pestaña nueva.
 *
 * Se pide primero una URL temporal —que el backend solo entrega después de comprobar que el
 * archivo es de este cliente— y se abre esa. Así el archivo no viaja por el servidor de la
 * aplicación y el navegador se encarga, que es lo que ya hace el sistema interno.
 */
export const abrirArchivo = async (documentoId, descargar = false) => {
  const { data } = await api.get(`/api/portal/documentos/${documentoId}/url`, {
    params: { descargar },
  });
  window.open(data.url, '_blank', 'noopener,noreferrer');
};

export default api;
